import { FC, useState } from 'react';
import {
    IconButton,
    Button,
    Icon,
    Icons,
    ButtonStyles,
    IconButtonStyles,
    IconButtonSizes,
    Status,
    StatusType
} from '@silverfort/sf-components';
import AlertsManagementFilters, { Filters } from './AlertsManagementFilters';
import WebhookDetails from '../../components/Webhook/WebhookDetails';
import { DEFAULT_WEBHOOK } from '../../components/Webhook/WebhookConsts';
import * as actions from '../../containers/AlertsManagement/AlertsManagementActions';
import { SUBCATEGORIES } from '../../common/categories';
import SyslogServerDetails from '../../components/SyslogServers/SyslogServersDetails';
import { DEFAULT_SYSLOG } from '../../components/SyslogServers/SyslogConsts';
import EmailDetails from '../../components/Email/EmailDetails';
import { DEFAULT_EMAIL } from '../../components/Email/EmailConsts';
import CustomIntegrationDetails from '../../components/CustomIntegration/CustomIntegrationDetails';
import {
    WebhookIntegration,
    SyslogServerIntegration,
    EmailIntegration,
    CustomIntegration,
    CustomIntegrationsSchema
} from './types';
import { useIsCloudOnly } from '../../hooks/featureFlags/useIsCloudOnly';

const { WEBHOOK, SYSLOG_SERVER, EMAIL } = SUBCATEGORIES;

const CLOUD_ONLY_CUSTOM_INTEGRATIONS = ['Slack'];

type GalleryItem = {
    id: string;
    providerName: string;
    providerType: string;
    activeIntegrations: number;
    formComponent: React.ReactNode;
} & ({ icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>; logo?: never } | { logo: string; icon?: never });

interface AlertsManagementGalleryProps {
    isEditor: boolean;
    hasHttpProxy: boolean;
    webhooks: Record<string, WebhookIntegration>;
    syslogServers: Record<string, SyslogServerIntegration>;
    emails: Record<string, EmailIntegration>;
    customIntegrations: Record<string, CustomIntegration[]>;
    customIntegrationsSchema: CustomIntegrationsSchema;
    visibleFields: Record<string, Record<string, unknown>>;
    saveCustomIntegrations: (providerKey: string, integrations: any) => void;
    onClose: () => void;
    dispatch: (action: { type: string; payload: any }) => void;
    save: (saveAll: boolean, subCategoryId: string) => void;
}

const AlertsManagementGallery: FC<AlertsManagementGalleryProps> = ({
    isEditor,
    hasHttpProxy,
    webhooks,
    syslogServers,
    emails,
    customIntegrations,
    customIntegrationsSchema,
    visibleFields,
    saveCustomIntegrations,
    onClose,
    dispatch,
    save
}) => {
    const [selectedGalleryItem, setSelectedGalleryItem] = useState<GalleryItem | undefined>();
    const [searchText, setSearchText] = useState('');
    const [selectedFilters, setSelectedFilters] = useState<Filters>({ providerType: [] });
    const isCloudOnly = useIsCloudOnly();

    const hasValues = [webhooks, syslogServers, emails, customIntegrations].some((value) => Object.keys(value).length);

    const onCancel = () => {
        setSearchText('');
        setSelectedFilters({ providerType: [] });
        setSelectedGalleryItem(undefined);
    };

    const onSave = (subCategoryId: string) => {
        save(false, subCategoryId);
        onClose();
    };

    const defaultGalleryItems: GalleryItem[] = [
        {
            id: SYSLOG_SERVER.ID,
            providerName: SYSLOG_SERVER.NAME,
            providerType: 'notifications',
            icon: Icons.Cube,
            activeIntegrations: Object.keys(syslogServers).length,
            formComponent: (
                <SyslogServerDetails
                    data={DEFAULT_SYSLOG}
                    otherSyslogServers={Object.values(syslogServers)}
                    onSave={(syslogSeverToAdd) => {
                        syslogSeverToAdd.enabled = true;
                        dispatch({ type: actions.ADD_SYSLOG_SERVER, payload: syslogSeverToAdd });
                        onSave(SYSLOG_SERVER.ID);
                    }}
                    onCancel={onCancel}
                />
            )
        },
        {
            id: WEBHOOK.ID,
            providerName: WEBHOOK.NAME,
            providerType: 'notifications',
            icon: Icons.Webhook,
            activeIntegrations: Object.keys(webhooks).length,
            formComponent: (
                <WebhookDetails
                    data={DEFAULT_WEBHOOK}
                    otherWebhooks={Object.values(webhooks)}
                    onSave={(webhookToAdd) => {
                        webhookToAdd.enabled = true;
                        dispatch({ type: actions.ADD_WEBHOOK, payload: webhookToAdd });
                        onSave(WEBHOOK.ID);
                    }}
                    onCancel={onCancel}
                />
            )
        },
        {
            id: EMAIL.ID,
            providerName: EMAIL.NAME,
            providerType: 'notifications',
            icon: Icons.Mail,
            activeIntegrations: Object.keys(emails).length,
            formComponent: (
                <EmailDetails
                    data={DEFAULT_EMAIL}
                    otherEmails={Object.values(emails)}
                    onSave={(emailToAdd) => {
                        emailToAdd.enabled = true;
                        dispatch({ type: actions.ADD_EMAIL, payload: emailToAdd });
                        onSave(EMAIL.ID);
                    }}
                    onCancel={onCancel}
                />
            )
        }
    ].filter((item) => item.id in visibleFields);

    const customIntegrationsItems = Object.entries(customIntegrationsSchema.properties ?? {})
        .map(([key, value]) => {
            return {
                id: key,
                providerName: value.name,
                providerType: value.integrationType,
                logo: value.logo,
                activeIntegrations: customIntegrations[key] ? customIntegrations[key].length : 0,
                formComponent: (
                    <CustomIntegrationDetails
                        customIntegrationsSchema={customIntegrationsSchema}
                        providerKey={key}
                        otherCustomIntegrations={customIntegrations[key]}
                        providerName={value.name}
                        logo={value.logo}
                        hasHttpProxy={hasHttpProxy}
                        onSave={(integrationValue) => {
                            integrationValue.enabled = true;
                            saveCustomIntegrations(key, [...(customIntegrations[key] ?? []), integrationValue]);
                            onClose();
                        }}
                        onCancel={onCancel}
                    />
                )
            };
        })
        .filter((item) => (isCloudOnly ? CLOUD_ONLY_CUSTOM_INTEGRATIONS.includes(item.providerName) : true));

    const galleryItems: GalleryItem[] = [...defaultGalleryItems, ...customIntegrationsItems]
        .filter((item) => (searchText ? item.providerName.toLowerCase().includes(searchText) : true))
        .filter((item) => {
            const { providerType } = item;
            const providerTypeFilter = selectedFilters['providerType'];
            return !providerTypeFilter.length || providerTypeFilter.includes(providerType);
        })
        .sort((a, b) => a.providerName?.localeCompare(b.providerName));

    if (selectedGalleryItem) {
        return (
            <div className="bg-sf-white h-[calc(100vh-62px)] py-5 px-6 overflow-y-auto">
                <div className="font-medium text-sf-gray-800 h-16">Add Integration</div>
                {selectedGalleryItem.formComponent}
            </div>
        );
    }

    return (
        <div className="bg-sf-white h-[calc(100vh-62px)] py-5 px-6 overflow-y-auto">
            <div className="font-medium text-sf-gray-800 h-16">
                {hasValues ? (
                    <div className="flex items-center gap-1">
                        <IconButton
                            buttonStyle={IconButtonStyles.Secondary}
                            icon={Icons.ArrowBack}
                            onClick={onClose}
                            sizeVariant={IconButtonSizes.Small}
                        />
                        New Integration
                    </div>
                ) : (
                    'Add New Integration'
                )}
            </div>
            <div>
                <AlertsManagementFilters
                    searchText={searchText}
                    onSearchChange={(text) => setSearchText(text)}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                />
            </div>
            <div className="flex gap-4 flex-wrap overflow-auto">
                {galleryItems.map((item) => (
                    <div
                        key={item.id}
                        className="mt-4 pt-6 w-80 h-52 b border rounded-lg border-sf-gray-100 text-sf-gray-800 flex flex-col justify-between">
                        <div className="pl-6">
                            <div className="flex items-center justify-center h-12 w-12 bg-sf-gray-50 rounded-lg">
                                {item.logo !== undefined ? (
                                    <img alt="integration logo" className="w-7 h-7" src={item.logo} />
                                ) : (
                                    <Icon
                                        className="text-sf-gray-400"
                                        icon={item.icon ?? Icons.IntegrationExternalFlat}
                                        size={30}
                                    />
                                )}
                            </div>
                            <div className="mt-4 font-medium">{item.providerName}</div>
                            {item.activeIntegrations ? (
                                <Status
                                    className="mt-2"
                                    text={`${item.activeIntegrations} Active integrations`}
                                    statusType={StatusType.Success}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="h-12 pr-4 border-t border-t-sf-gray-100 flex items-center flex-row-reverse">
                            <Button
                                buttonStyle={ButtonStyles.Primary}
                                iconLeft={Icons.Add}
                                onClick={() => {
                                    setSelectedGalleryItem(item);
                                }}
                                disabled={!isEditor}
                                text="Add Integration"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AlertsManagementGallery;
